import { arrayMove } from '@dnd-kit/sortable';
import { ActionIcon, Button, Card, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  BROWSER_STEP_TYPES,
  MAX_BROWSER_STEPS,
  MAX_SELECTOR_LENGTH,
  STEP_TIMEOUT_MS,
  WAIT_MS,
  blankBrowserStep,
  browserStepErrors,
  changeBrowserStepType,
  stepFields,
} from '../../helpers/knowledgeIngest';

const STEP_HINTS = {
  waitForSelector:
    'Waits in the headless browser until the element is on the page, up to the timeout. The import continues either way.',
  click: 'Waits for the element, then clicks it (hidden buttons too) — e.g. to dismiss a banner or open a tab.',
  wait: 'Pauses the headless browser for a fixed time, e.g. for content that loads late.',
  remove: 'Strips every matching element from the captured page before its text is extracted.',
};

const SELECTOR_PLACEHOLDERS = { waitForSelector: '#main-content', click: '#accept-cookies', remove: '.cookie-banner' };

// An empty field is reported by Save, not while the row is still being filled in.
const shownError = (value, message) => (value === '' || value == null ? null : message);

/**
 * Ordered browser steps. Controlled: `onChange` gets the whole new list, every row already
 * holding only its type's keys (changeBrowserStepType). Rows are keyed by position like
 * FunnelFormModal's steps — no row ids exist, so none can ride along into the saved value.
 */
const BrowserStepsEditor = ({ steps, onChange }) => {
  const replaceStep = (index, step) =>
    onChange(steps.map((current, position) => (position === index ? step : current)));
  const updateStep = (index, changes) => replaceStep(index, { ...steps[index], ...changes });

  return (
    <Stack gap="xs">
      {steps.length === 0 && (
        <Text size="sm" c="dimmed">
          No browser steps.
        </Text>
      )}

      {steps.map((step, index) => {
        const n = index + 1;
        const fields = stepFields(step.type);
        const errors = browserStepErrors(step);

        return (
          <Card key={index} withBorder padding="sm">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>
                Step {n}
              </Text>
              <Group gap={4}>
                <ActionIcon
                  variant="default"
                  aria-label={`Move step ${n} up`}
                  disabled={index === 0}
                  onClick={() => onChange(arrayMove(steps, index, index - 1))}
                >
                  <IconArrowUp size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  aria-label={`Move step ${n} down`}
                  disabled={index === steps.length - 1}
                  onClick={() => onChange(arrayMove(steps, index, index + 1))}
                >
                  <IconArrowDown size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  color="red"
                  aria-label={`Remove step ${n}`}
                  onClick={() => onChange(steps.filter((_, position) => position !== index))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>

            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Select
                w={190}
                aria-label={`Step ${n} action`}
                data={BROWSER_STEP_TYPES}
                value={step.type}
                allowDeselect={false}
                error={errors.type}
                onChange={(type) => type && replaceStep(index, changeBrowserStepType(step, type))}
              />
              {fields.includes('ms') && (
                <NumberInput
                  w={150}
                  aria-label={`Step ${n} duration`}
                  suffix=" ms"
                  min={WAIT_MS.min}
                  max={WAIT_MS.max}
                  allowDecimal={false}
                  allowNegative={false}
                  value={step.ms}
                  error={shownError(step.ms, errors.ms)}
                  onChange={(ms) => updateStep(index, { ms })}
                />
              )}
              {fields.includes('timeoutMs') && (
                <NumberInput
                  w={150}
                  aria-label={`Step ${n} timeout`}
                  suffix=" ms"
                  min={STEP_TIMEOUT_MS.min}
                  max={STEP_TIMEOUT_MS.max}
                  allowDecimal={false}
                  allowNegative={false}
                  value={step.timeoutMs}
                  error={shownError(step.timeoutMs, errors.timeoutMs)}
                  onChange={(timeoutMs) => updateStep(index, { timeoutMs })}
                />
              )}
            </Group>

            {fields.includes('selector') && (
              <TextInput
                mt="xs"
                aria-label={`Step ${n} selector`}
                placeholder={SELECTOR_PLACEHOLDERS[step.type]}
                maxLength={MAX_SELECTOR_LENGTH}
                value={step.selector}
                error={shownError(step.selector, errors.selector)}
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                // Read the value here, never inside a state updater (see FunnelFormModal.test.jsx).
                onChange={(event) => updateStep(index, { selector: event.currentTarget.value })}
              />
            )}

            <Text size="xs" c="dimmed" mt={4}>
              {STEP_HINTS[step.type]}
            </Text>
          </Card>
        );
      })}

      <Button
        variant="default"
        leftSection={<IconPlus size={16} />}
        disabled={steps.length >= MAX_BROWSER_STEPS}
        onClick={() => onChange([...steps, blankBrowserStep()])}
        style={{ alignSelf: 'flex-start' }}
      >
        Add step
      </Button>
    </Stack>
  );
};

export default BrowserStepsEditor;
